import {
  isBaseCurrency,
  isBaseUnitOfMeasure,
  needsPriceConversion,
  usdLbToCustomerPrice,
  customerPriceToUsdLb,
} from './priceConversion';

// Rates as returned by Api::V1::ExchangeRatesController#rate_per_currency_unit
// for measurement_unit_from: unit_lb / measurement_unit_to: unit_kg.
const LB_TO_KG = 0.4536;

describe('isBaseCurrency / isBaseUnitOfMeasure', () => {
  it('recognises the currency and unit prices are captured in', () => {
    expect(isBaseCurrency('USD')).toBe(true);
    expect(isBaseCurrency('AUD')).toBe(false);
    expect(isBaseCurrency(undefined)).toBe(false);

    expect(isBaseUnitOfMeasure('unit_lb')).toBe(true);
    expect(isBaseUnitOfMeasure('unit_kg')).toBe(false);
    expect(isBaseUnitOfMeasure(undefined)).toBe(false);
  });
});

describe('needsPriceConversion', () => {
  const cases = [
    { currency: 'USD', unit: 'unit_lb', expected: false, why: 'already USD/lb' },
    {
      currency: 'USD',
      unit: 'unit_kg',
      expected: true,
      why: 'PLA-1005: only the unit differs',
    },
    {
      currency: 'GBP',
      unit: 'unit_lb',
      expected: true,
      why: 'PLA-1005: only the currency differs',
    },
    { currency: 'AUD', unit: 'unit_kg', expected: true, why: 'both differ' },
  ];

  cases.forEach(({ currency, unit, expected, why }) => {
    it(`returns ${expected} for ${currency}/${unit} (${why})`, () => {
      expect(
        needsPriceConversion(isBaseCurrency(currency), isBaseUnitOfMeasure(unit))
      ).toBe(expected);
    });
  });

  it('does not require BOTH dimensions to differ', () => {
    // Regression guard for PLA-1005: the condition used to be
    // `!sameCurrency && !sameUnitOfMeasure`, which skipped the conversion for
    // every customer that differed in exactly one dimension.
    expect(needsPriceConversion(true, false)).toBe(true);
    expect(needsPriceConversion(false, true)).toBe(true);
  });
});

describe('usdLbToCustomerPrice', () => {
  it('converts USD/lb to USD/kg for a USD/kg customer (PLA-1005)', () => {
    // Hoffmann Industries Ltd (CUS-1014): currency USD, unit_of_measure unit_kg.
    // Same currency, so the endpoint returns exchange_rate 1.0.
    const result = usdLbToCustomerPrice(5.2, {
      measurementRate: LB_TO_KG,
      customerRate: 1,
    });

    expect(result).toBeCloseTo(11.4638, 4);
  });

  it('converts currency and unit at once for an AUD/kg customer', () => {
    // Endpoint returns exchange_rate USD -> AUD = 1.5; the component inverts it.
    const result = usdLbToCustomerPrice(5.2, {
      measurementRate: LB_TO_KG,
      customerRate: 1 / 1.5,
    });

    // 5.2 USD/lb == 11.4638 USD/kg == 17.1958 AUD/kg
    expect(result).toBeCloseTo(17.195767, 6);
    // ...which is what the component writes into the field.
    expect(result.toFixed(4)).toEqual('17.1958');
  });

  it('accepts the string values coming from the form inputs', () => {
    const result = usdLbToCustomerPrice('5.2', {
      measurementRate: String(LB_TO_KG),
      customerRate: '1',
    });

    expect(result).toBeCloseTo(11.4638, 4);
  });
});

describe('customerPriceToUsdLb', () => {
  it('is the inverse of usdLbToCustomerPrice', () => {
    const rates = { measurementRate: LB_TO_KG, customerRate: 1 / 1.5 };

    expect(customerPriceToUsdLb(17.195767, rates)).toBeCloseTo(5.2, 4);
  });

  it('round-trips a price through both conversions', () => {
    const rates = { measurementRate: LB_TO_KG, customerRate: 1 / 1.5 };
    const original = 3.75;

    expect(
      customerPriceToUsdLb(usdLbToCustomerPrice(original, rates), rates)
    ).toBeCloseTo(original, 6);
  });
});
